import { FieldArray, useFormikContext } from 'formik';
import { Plus, Trash2 } from 'lucide-react';
import Button from 'shared/components/Button';
import FormField from 'shared/components/form/FormField';
import FormikInput from 'shared/components/form/FormikInput';
import StyledAutocomplete from 'shared/components/StyledAutocomplete';
import InternationalPhones from 'shared/components/phone/InternationalPhones';
import { createPhoneValidationTest } from 'shared/components/phone/phoneValidation';
import * as Yup from 'yup';
import { ApplicantRoleModel } from '../../models/ApplicantRole';
import {
  EventoReferentFormValues,
  EventoWizardFormValues,
  emptyReferent
} from './eventoWizardTypes';

export const referentsFields = ['referents'] as const;

export const referentsSchema = Yup.object().shape({
  referents: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('El nombre es requerido'),
        roleId: Yup.number().nullable().required('El rol es requerido'),
        email: Yup.string().email('Email inválido'),
        phone: Yup.string().test(
          'phone-validation',
          'Número de teléfono inválido',
          createPhoneValidationTest('AR', true)
        )
      })
    )
    .min(1, 'Se necesita al menos un referente')
});

interface Props {
  roles: ApplicantRoleModel[];
  isSubmitting: boolean;
  onBack: () => void;
}

const EventoReferentsStep = ({ roles, isSubmitting, onBack }: Props) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<EventoWizardFormValues>();

  return (
    <div className="flex max-w-2xl flex-col gap-5">
      <h2 className="text-xl font-semibold text-ink dark:text-ink-dark">
        Referentes
      </h2>
      <p className="text-sm text-inkMuted dark:text-inkMuted-dark">
        Al menos un referente de contacto es obligatorio para crear el evento.
      </p>

      <FieldArray name="referents">
        {({ push, remove }) => (
          <div className="flex flex-col gap-4">
            {values.referents.map((_, index) => {
              const referentErrors =
                (errors.referents?.[index] as
                  | Partial<Record<keyof EventoReferentFormValues, string>>
                  | undefined) ?? {};
              const referentTouched =
                (touched.referents?.[index] as
                  | Partial<Record<keyof EventoReferentFormValues, boolean>>
                  | undefined) ?? {};
              return (
                <div
                  key={index}
                  className="flex flex-col gap-3 rounded-2xl border border-secondary-100 bg-white p-4 dark:border-white/10 dark:bg-darkLight"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-ink dark:text-ink-dark">
                      Referente {index + 1}
                    </span>
                    {values.referents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-inkMuted-dark transition hover:text-red-500"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <FormikInput
                      name={`referents.${index}.name`}
                      label="Nombre"
                    />
                    <FormField
                      label="Rol"
                      error={referentTouched.roleId && referentErrors.roleId}
                    >
                      <StyledAutocomplete<ApplicantRoleModel>
                        options={roles}
                        value={
                          roles.find(
                            (role) => role.id === values.referents[index].roleId
                          ) ?? null
                        }
                        getOptionLabel={(role) => role?.name ?? ''}
                        onChange={(value) =>
                          setFieldValue(
                            `referents.${index}.roleId`,
                            value ? value.id : null
                          )
                        }
                        onBlur={() =>
                          setFieldTouched(`referents.${index}.roleId`, true)
                        }
                        placeholder="Seleccionar..."
                      />
                    </FormField>
                    <FormikInput
                      name={`referents.${index}.email`}
                      label="Email (opcional)"
                    />
                    <InternationalPhones
                      name={`referents.${index}.phone`}
                      label="Teléfono (opcional)"
                    />
                  </div>
                </div>
              );
            })}

            <Button
              type="button"
              variant="dashed"
              onClick={() => push({ ...emptyReferent })}
              className="justify-center"
            >
              <Plus size={15} />
              Agregar otro referente
            </Button>
          </div>
        )}
      </FieldArray>

      <div className="flex justify-between pt-4">
        <Button type="button" variant="secondary" onClick={onBack}>
          Atrás
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creando evento...' : 'Crear evento'}
        </Button>
      </div>
    </div>
  );
};

export default EventoReferentsStep;
